// src/components/intentions/EmploymentDetailsSection.tsx
import React from 'react';
import { Info, Calendar, Briefcase, Users, AlertTriangle } from 'lucide-react';
import { EmploymentDetailsData, EmploymentType, IntentionResponse, LeaveType } from '../../types/intentionPeriods';

interface EmploymentDetailsSectionProps {
  employmentDetails: EmploymentDetailsData;
  onChange: (details: EmploymentDetailsData) => void;
  disabled?: boolean;
}

const EmploymentDetailsSection: React.FC<EmploymentDetailsSectionProps> = ({
  employmentDetails,
  onChange,
  disabled = false
}) => {
  const handleEmploymentTypeChange = (type: EmploymentType) => {
    onChange({
      ...employmentDetails,
      employmentType: type,
      // Reset the opposing field when changing type
      intentionToReturn: type === 'ongoing' ? employmentDetails.intentionToReturn : null,
      wishToBeConsidered: type === 'fixed-term' ? employmentDetails.wishToBeConsidered : null
    });
  };

  const handleIntentionChange = (value: IntentionResponse) => {
    if (employmentDetails.employmentType === 'ongoing') {
      onChange({
        ...employmentDetails,
        intentionToReturn: value,
        // Clear reason if returning
        notReturningReason: value === 'yes' ? '' : employmentDetails.notReturningReason
      });
    } else {
      onChange({
        ...employmentDetails,
        wishToBeConsidered: value,
        // Clear reason if being considered
        notReturningReason: value === 'yes' ? '' : employmentDetails.notReturningReason
      });
    }
  };

  const handleReasonChange = (reason: string) => {
    onChange({
      ...employmentDetails,
      notReturningReason: reason
    });
  };

  const handleLeavePlansToggle = (hasPlans: boolean) => {
    onChange({
      ...employmentDetails,
      hasLeavePlans: hasPlans,
      // Clear leave details if no plans
      leaveType: hasPlans ? employmentDetails.leaveType : null,
      leaveStartDate: hasPlans ? employmentDetails.leaveStartDate : undefined,
      leaveEndDate: hasPlans ? employmentDetails.leaveEndDate : undefined
    });
  };

  const handleLeaveTypeChange = (type: LeaveType) => {
    onChange({
      ...employmentDetails,
      leaveType: type
    });
  };

  const handleLeaveStartDateChange = (date: string) => {
    onChange({
      ...employmentDetails,
      leaveStartDate: date
    });
  };

  const handleLeaveEndDateChange = (date: string) => {
    onChange({
      ...employmentDetails,
      leaveEndDate: date
    });
  };

  const handleYear9StretchChange = (value: boolean | null) => {
    onChange({
      ...employmentDetails,
      year9StretchInterest: value
    });
  };

  // Determine which intention question to show based on employment type
  const currentIntention = employmentDetails.employmentType === 'ongoing'
    ? employmentDetails.intentionToReturn
    : employmentDetails.wishToBeConsidered;

  const showReasonField = currentIntention === 'no' || currentIntention === 'uncertain';
  const showSpecialRoles = currentIntention === 'yes' || currentIntention === 'uncertain';

  return (
    <div className="bg-white rounded-lg border border-ink-200 p-6">
      <h3 className="text-lg font-semibold text-ink-900 mb-4 flex items-center gap-2">
        <Briefcase className="w-5 h-5 text-ink-500" />
        Employment Details
      </h3>

      {/* Employment Type */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-ink-700 mb-2">
          Employment Type <span className="text-danger-fg">*</span>
        </label>
        <div className="flex gap-4">
          <label className={`flex items-center gap-2 px-4 py-3 border rounded-lg cursor-pointer transition-colors ${
            employmentDetails.employmentType === 'ongoing'
              ? 'border-navy-600 bg-navy-50 text-navy-900'
              : 'border-ink-300 bg-white hover:bg-ink-50'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
            <input
              type="radio"
              name="employmentType"
              value="ongoing"
              checked={employmentDetails.employmentType === 'ongoing'}
              onChange={() => handleEmploymentTypeChange('ongoing')}
              disabled={disabled}
              className="sr-only"
            />
            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
              employmentDetails.employmentType === 'ongoing'
                ? 'border-navy-600'
                : 'border-ink-400'
            }`}>
              {employmentDetails.employmentType === 'ongoing' && (
                <div className="w-2 h-2 bg-navy-600 rounded-full" />
              )}
            </div>
            <span className="font-medium">Ongoing</span>
          </label>

          <label className={`flex items-center gap-2 px-4 py-3 border rounded-lg cursor-pointer transition-colors ${
            employmentDetails.employmentType === 'fixed-term'
              ? 'border-navy-600 bg-navy-50 text-navy-900'
              : 'border-ink-300 bg-white hover:bg-ink-50'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
            <input
              type="radio"
              name="employmentType"
              value="fixed-term"
              checked={employmentDetails.employmentType === 'fixed-term'}
              onChange={() => handleEmploymentTypeChange('fixed-term')}
              disabled={disabled}
              className="sr-only"
            />
            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
              employmentDetails.employmentType === 'fixed-term'
                ? 'border-navy-600'
                : 'border-ink-400'
            }`}>
              {employmentDetails.employmentType === 'fixed-term' && (
                <div className="w-2 h-2 bg-navy-600 rounded-full" />
              )}
            </div>
            <span className="font-medium">Fixed-Term</span>
          </label>
        </div>
      </div>

      {/* Intention Question (conditional based on employment type) */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-ink-700 mb-2">
          {employmentDetails.employmentType === 'ongoing'
            ? 'Do you intend to return next year?'
            : 'Do you wish to be considered for employment next year?'
          } <span className="text-danger-fg">*</span>
        </label>
        <div className="flex flex-wrap gap-3">
          {(['yes', 'no', 'uncertain'] as IntentionResponse[]).map((option) => (
            <label
              key={option}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg cursor-pointer transition-colors ${
                currentIntention === option
                  ? option === 'yes'
                    ? 'border-ok-border bg-ok-bg text-ok-fg'
                    : option === 'no'
                      ? 'border-danger-border bg-danger-bg text-danger-fg'
                      : 'border-warn-border bg-warn-bg text-warn-fg'
                  : 'border-ink-300 bg-white hover:bg-ink-50'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <input
                type="radio"
                name="intention"
                value={option}
                checked={currentIntention === option}
                onChange={() => handleIntentionChange(option)}
                disabled={disabled}
                className="sr-only"
              />
              <span className="capitalize font-medium">{option}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Warning when selecting No */}
      {currentIntention === 'no' && (
        <div className="mb-6 p-4 bg-warn-bg border border-warn-border rounded-lg">
          <div className="flex gap-3">
            <AlertTriangle className="w-5 h-5 text-warn-fg flex-shrink-0 mt-0.5" />
            <div className="text-sm text-warn-fg">
              <p className="font-medium mb-1">Planning to take leave?</p>
              <p>
                If you wish to remain employed but are not returning due to leave or other arrangements,
                please select <strong>"Yes"</strong> and indicate your leave plans in the section that follows.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Reason field (shown if No or Uncertain) */}
      {showReasonField && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-ink-700 mb-2">
            Please provide a reason or additional context
          </label>
          <textarea
            value={employmentDetails.notReturningReason || ''}
            onChange={(e) => handleReasonChange(e.target.value)}
            disabled={disabled}
            rows={3}
            className="w-full px-3 py-2 border border-ink-300 rounded-md shadow-sm focus:ring-navy-500 focus:border-navy-500 text-sm"
            placeholder="Please share any details that would help us understand your situation..."
          />
        </div>
      )}

      {/* Leave Plans Section - Only shown if returning/being considered */}
      {showSpecialRoles && (
      <div className="mb-6 p-4 bg-ink-50 rounded-lg border border-ink-200">
        <div className="flex items-center justify-between mb-4">
          <label className="flex items-center gap-2 text-sm font-medium text-ink-700">
            <Calendar className="w-4 h-4" />
            Do you have planned leave for next year?
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => handleLeavePlansToggle(true)}
              disabled={disabled}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                employmentDetails.hasLeavePlans
                  ? 'bg-navy-600 text-white'
                  : 'bg-white border border-ink-300 text-ink-700 hover:bg-ink-50'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              Yes
            </button>
            <button
              type="button"
              onClick={() => handleLeavePlansToggle(false)}
              disabled={disabled}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                !employmentDetails.hasLeavePlans
                  ? 'bg-navy-600 text-white'
                  : 'bg-white border border-ink-300 text-ink-700 hover:bg-ink-50'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              No
            </button>
          </div>
        </div>

        {employmentDetails.hasLeavePlans && (
          <div className="space-y-4 pt-4 border-t border-ink-200">
            {/* Leave Type */}
            <div>
              <label className="block text-sm font-medium text-ink-700 mb-2">
                Leave Type <span className="text-danger-fg">*</span>
              </label>
              <select
                value={employmentDetails.leaveType || ''}
                onChange={(e) => handleLeaveTypeChange(e.target.value as LeaveType)}
                disabled={disabled}
                className="w-full px-3 py-2 border border-ink-300 rounded-md shadow-sm focus:ring-navy-500 focus:border-navy-500 text-sm"
              >
                <option value="">Select leave type...</option>
                <option value="long-service">Long Service Leave</option>
                <option value="leave-without-pay">Leave Without Pay</option>
                <option value="other">Other Leave</option>
              </select>
            </div>

            {/* Leave Dates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-2">
                  Leave Start Date <span className="text-danger-fg">*</span>
                </label>
                <input
                  type="date"
                  value={employmentDetails.leaveStartDate || ''}
                  onChange={(e) => handleLeaveStartDateChange(e.target.value)}
                  disabled={disabled}
                  className="w-full px-3 py-2 border border-ink-300 rounded-md shadow-sm focus:ring-navy-500 focus:border-navy-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-2">
                  Leave End Date <span className="text-danger-fg">*</span>
                </label>
                <input
                  type="date"
                  value={employmentDetails.leaveEndDate || ''}
                  onChange={(e) => handleLeaveEndDateChange(e.target.value)}
                  disabled={disabled}
                  min={employmentDetails.leaveStartDate || undefined}
                  className="w-full px-3 py-2 border border-ink-300 rounded-md shadow-sm focus:ring-navy-500 focus:border-navy-500 text-sm"
                />
              </div>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Special Roles Section - Only shown if returning/being considered */}
      {showSpecialRoles && (
        <div className="p-4 bg-navy-50 rounded-lg border border-navy-200">
          <h4 className="text-sm font-semibold text-navy-900 mb-4 flex items-center gap-2">
            <Users className="w-4 h-4" />
            Special Role Interest
          </h4>

          {/* Year 9 Stretch Program */}
          <div className="p-3 bg-white rounded-md border border-navy-100">
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <label className="block text-sm font-medium text-ink-900 mb-1">
                  Year 9 Stretch Program <span className="text-danger-fg">*</span>
                </label>
                <div className="flex items-start gap-2 text-xs text-ink-500 mb-3">
                  <Info className="w-4 h-4 flex-shrink-0 text-navy-500 mt-0.5" />
                  <span>
                    Staff involved in the Year 9 Stretch Program have approximately 75% of a regular teaching load
                    to accommodate the additional responsibilities of the program.
                  </span>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleYear9StretchChange(true)}
                    disabled={disabled}
                    className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                      employmentDetails.year9StretchInterest === true
                        ? 'bg-ok-solid text-white'
                        : 'bg-white border border-ink-300 text-ink-700 hover:bg-ink-50'
                    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    Interested
                  </button>
                  <button
                    type="button"
                    onClick={() => handleYear9StretchChange(false)}
                    disabled={disabled}
                    className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                      employmentDetails.year9StretchInterest === false
                        ? 'bg-ink-500 text-white'
                        : 'bg-white border border-ink-300 text-ink-700 hover:bg-ink-50'
                    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    Not Interested
                  </button>
                  <button
                    type="button"
                    onClick={() => handleYear9StretchChange(null)}
                    disabled={disabled}
                    className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                      employmentDetails.year9StretchInterest === null
                        ? 'bg-warn-solid text-white'
                        : 'bg-white border border-ink-300 text-ink-700 hover:bg-ink-50'
                    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    No Preference
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmploymentDetailsSection;
